import { call, put, takeLatest } from 'redux-saga/effects'
import store from 'store'
import history from '../routers/history'
import {
  GET_CHECK_GROUP_REQUEST,
  POST_CREATE_GROUP_REQUEST,
  POST_CREATE_GROUP_FAILURE,
  GET_GROUP_REQUEST,
  GET_GROUP_FAILURE,
  getGroupSuccess,
  UPDATE_GROUP_FAILURE,
  UPDATE_GROUP_REQUEST,
  getGroupRequest,
  postCreateGroupSuccess,
  POST_ASSIGN_GROUP_REQUEST,
  postAssignGroupSuccess,
  POST_ASSIGN_GROUP_FAILURE,
  postCreateGroupRequest,
  postCreateGroupSimilarGroups,
  postCreateGroupDomainBlocked,
} from '../actions/groupActions'
import { getGroupAPICall, postCreateGroupAPICall, postUpdateGroupAPICall } from '../apis/group'
import { getCurrentIPInfoAPICall } from '../apis/location'
import { postNotificationToSlackAPICall, postAdminSignUpInfoToZapierAPICall, postNonAdminSignUpInfoToZapierAPICall } from '../apis/slack'
import { SIGN_UP_STEPS, USER_FORM_NAME } from '../config/form-settings'
import { INVITE_ORG, LOCAL_STORAGE_NEW_ORGANIZATION_KEY, LOCAL_STORAGE_NEW_FACILITY_KEY, LOCAL_STORAGE_COUNTRY_LIST_KEY, LOCAL_STORAGE_APPROVED_FACILITIES, LOCAL_STORAGE_GROUP_ID, groupSourceConstant, LOCAL_STORAGE_EXPEDITED_SIGNUP } from '../config/constants'
import { getAzureAddressApiCall } from '../apis/options'
import { getUpdatedUserApiCall, patchExistingUserAPICall, patchUserMetadataApiCall } from '../apis/user'
// import { uploadFile } from '../utils/azure-blob-helpers'
import {
  clearNotification,
  dataLoadingNotification
} from '../utils/notification-helpers'
// import { v4 as uuidV4 } from 'uuid'
import AuthService from '../services/AuthService'
import { postCreateFacilityRequest } from '../actions/facilityActions'
import { postCreateFacilityAPICall } from '../apis/facility'
import { t } from "../i18n";
import { postDemoStudyAPICall } from '../apis/medicialGroup'
import { isEmpty } from 'lodash';
import { startSubmit, stopSubmit } from 'redux-form'
import { getOrganizationDomainListRequest } from '../actions/medicialGroupActions'
import {
  classifyCreateGroupResponse,
  GROUP_CREATE_OUTCOME,
  normalizeGroupCreatePayload,
  sortSimilarGroups
} from '../utils/group-dedupe'

const DEFAULT_AI_PREFERENCES = [
  'ai_report_generation',
  'ai_qc_review',
  'ai_cpt_tagging'
]

const compactZapierPayload = (payload) => Object.keys(payload).reduce((acc, key) => {
  const value = payload[key]
  if (value !== undefined && value !== null && value !== '') {
    acc[key] = value
  }
  return acc
}, {})

const resolveOrganizationTypes = (orgDetails = {}) => {
  if (!Array.isArray(orgDetails.organizationType)) return undefined

  if (orgDetails.organizationType.includes('Other') && orgDetails.organizationTypeOther) {
    return orgDetails.organizationType
      .filter(type => type !== 'Other')
      .concat(orgDetails.organizationTypeOther)
  }

  return orgDetails.organizationType
}

const buildOrganizationDetailsZapierPayload = (payload = {}) => {
  const orgDetails = payload.organizationDetails || {}
  const aiPreferences = resolveAiPreferences(orgDetails, { defaultToEnabled: Boolean(payload.organizationDetails) })

  return compactZapierPayload({
    organization_name: payload.userGroupName,
    title: orgDetails.title,
    credentials: orgDetails.credentials,
    specialty: orgDetails.specialty,
    organization_structure: orgDetails.orgStructure,
    organization_type: resolveOrganizationTypes(orgDetails),
    organization_type_other: orgDetails.organizationTypeOther,
    location: orgDetails.location,
    load_sample_studies: payload.loadSampleStudies === true,
    ai_preferences: aiPreferences,
    ai_report_generation_and_dictation: aiPreferences.includes('ai_report_generation'),
    quality_control_review_and_alerts: aiPreferences.includes('ai_qc_review'),
    cpt_icd10_auto_tagging: aiPreferences.includes('ai_cpt_tagging'),
    role_in_facility: orgDetails.roleInFacility,
    facility_type: orgDetails.facilityType,
    modalities: orgDetails.modalities
  })
}

const resolveAiPreferences = (orgDetails = {}, options = {}) => {
  if (Array.isArray(orgDetails.aiPreferences)) return orgDetails.aiPreferences

  if (options.defaultToEnabled && !orgDetails.facilityType && !orgDetails.roleInFacility) {
    return DEFAULT_AI_PREFERENCES
  }

  return []
}

const buildAiUserMetadata = (orgDetails = {}) => {
  const aiPreferences = resolveAiPreferences(orgDetails, { defaultToEnabled: true })
  const isAiReportingEnabled = aiPreferences.includes('ai_report_generation')

  return {
    enable_ai_reporting: isAiReportingEnabled,
    enable_ai_dictation: isAiReportingEnabled,
    enable_ai_template_selection: isAiReportingEnabled,
    report_style: 'free_text'
  }
}


function * getCheckGroupHandler ({ payload }) {
  // Retire the old GET-by-name pre-check. All create entry points now use the
  // POST contract so name dedupe has one source of truth and one prompt.
  // Legacy facility containers dispatch the organization name as a string,
  // while the create saga requires an object payload.
  yield put(postCreateGroupRequest(normalizeGroupCreatePayload(payload)))
}

function * getGroupHandler () {
  try {
    const { responseOk, responseJSON } = yield call(getGroupAPICall)
    if (responseOk) {
      yield put(getGroupSuccess(responseJSON))
    }
  } catch (e) {
    console.error('ERROR: ', e)
    yield put({
      type: GET_GROUP_FAILURE,
      message: e.message
    })
  }
}
// we are updating postCreateGroupHandler to include getCheckGroupHandler since figma v1.65 to make create group using single flow
function* postCreateGroupHandler({ payload }) {
  const response = yield call(getCurrentIPInfoAPICall);
  const countryList = store.get(LOCAL_STORAGE_COUNTRY_LIST_KEY)
  const getUser = yield call(getUpdatedUserApiCall)
  try {
    if (!payload.isExpedite) yield put(startSubmit(USER_FORM_NAME));
    dataLoadingNotification(t("group_saga.processing"))
    const { userGroupName, userIsIndependentRadiologist, redirect = false, isExpedite } = payload || {}
    const country = response?.country ?? null;

    const auth = new AuthService()
    const userProfile = auth.getUserProfile()
    let countryName = null;
    if (country && countryList) {
      const singleCountryFound = countryList.find(singleCountry => singleCountry.alpha2Code === country || singleCountry.alpha3Code === country)
      countryName = singleCountryFound.name
    }

    const orgDetails = payload.organizationDetails || {}

    // Resolve organization_type array: replace "Other" entry with free-text value
    const resolvedOrgTypes = resolveOrganizationTypes(orgDetails)

    // User-entered location overrides IP-detected address
    const address = orgDetails.location
      ? { country: orgDetails.location }
      : country ? { country: countryName || "" } : undefined

    const createGroupResult = yield call(postCreateGroupAPICall, {
      ...payload,
      email_address: userProfile && userProfile.email,
      address,
      accountType: isExpedite ? 'referral' : 'full',
      organization_type: resolvedOrgTypes,
      org_structure: orgDetails.orgStructure || undefined
    })
    const {
      responseOk: createGroupResponseOk,
      responseStatus: createGroupResponseStatus,
      responseJSON: createGroupResponse,
      error: createGroupError
    } = createGroupResult || {}
    const createGroupOutcome = classifyCreateGroupResponse(createGroupResult)

    if (createGroupOutcome === GROUP_CREATE_OUTCOME.SIMILAR_GROUPS) {
      clearNotification()
      if (!payload.isExpedite) yield put(stopSubmit(USER_FORM_NAME))
      yield put(postCreateGroupSimilarGroups({
        similarGroups: sortSimilarGroups(createGroupResponse.similar_groups),
        // Keep the complete original payload so confirming the gate cannot
        // discard role-specific fields, logo data, or sample-study choice.
        pendingPayload: payload
      }))
      return
    }

    if (createGroupOutcome === GROUP_CREATE_OUTCOME.DOMAIN_BLOCKED) {
      clearNotification()
      if (!payload.isExpedite) yield put(stopSubmit(USER_FORM_NAME))
      yield put(postCreateGroupDomainBlocked({ responseJSON: createGroupResponse }))
      // Refresh the established domain-match flow. If it returns an
      // organization, the blocking dialog can route directly into its
      // existing request-access action.
      yield put(getOrganizationDomainListRequest())
      return
    }

    if (createGroupOutcome === GROUP_CREATE_OUTCOME.FAILED) {
      const errorMessage = createGroupError?.message ||
        createGroupResponse?.detail ||
        createGroupResponse?.message ||
        `Group creation failed with status ${createGroupResponseStatus || 'unknown'}`
      throw new Error(errorMessage)
    }

    if (createGroupResponseOk) {
      // Do not clear the expedited flag here. Older code wrote the string "null"
      // via setItem, which broke post-login routing, and clearing before
      // getNewTokensForAPI made session-recovery think this was a full signup.
      const groupId = createGroupResponse && createGroupResponse['group_id'];
      store.set(LOCAL_STORAGE_NEW_ORGANIZATION_KEY, groupId)
      store.set(INVITE_ORG, { groupId, groupName: userGroupName })
      const userId = userProfile && userProfile.sub
      const updatedAppMetadata = {
        app_metadata: {
          affiliated_group: groupId,
          admin: '1',
          main: '1',
          approved_facilities: [],
          associated_groups: []
        }
      }
      const { responseOk: userUpdateOk, responseJSON: userResponseJSON } = yield call(patchExistingUserAPICall, userId, { appMetadata: updatedAppMetadata })
      if (userUpdateOk) {
        // Persist user-level fields to Auth0 user_metadata (all roles)
        if (payload.organizationDetails) {
          const orgDetails = payload.organizationDetails
          const metadataUpdate = buildAiUserMetadata(orgDetails)
          if (orgDetails.title) metadataUpdate.title = orgDetails.title
          if (orgDetails.credentials) metadataUpdate.credentials = orgDetails.credentials
          if (Array.isArray(orgDetails.specialty) && orgDetails.specialty.length > 0) metadataUpdate.specialty = orgDetails.specialty

          if (Object.keys(metadataUpdate).length > 0) {
            yield call(patchUserMetadataApiCall, metadataUpdate)
          }
        }

        yield put(getGroupSuccess(userResponseJSON))
        if (userIsIndependentRadiologist && userIsIndependentRadiologist.isIndependentRadiologist) {
          // yield call(postNotificationToSlackAPICall, { title: '[Create Organization]:', description: 'Successfully created organization', jsonContent: createGroupResponse })
          yield call(postAdminSignUpInfoToZapierAPICall, {
            jsonContent: {
              ...userResponseJSON,
              ...createGroupResponse,
              ...buildOrganizationDetailsZapierPayload(payload)
            },
            options: { source: groupSourceConstant.creating_new_group }
          })
          const { userEmail, userPassword, postLoginTarget } = userIsIndependentRadiologist
          auth.login(userEmail, userPassword, postLoginTarget)
          clearNotification()
        } else {
          const accessTokenOk = yield call(auth.getNewTokensForAPI)
          if (typeof accessTokenOk === "string") {
            auth.updateUserAppMetadataInLocalStorage(userResponseJSON.app_metadata)
            const { userGroupLogo } = payload
            let containerURL;
            if (userGroupLogo && userGroupLogo[0]) {
              containerURL = yield call(getAzureAddressApiCall, 'grouplogos')
            }
            // Build AI settings from form toggles for PATCH /group
            const aiPrefs = resolveAiPreferences(orgDetails, { defaultToEnabled: Boolean(payload.organizationDetails) })
            const aiSettings = aiPrefs.length > 0 ? {
              aiReportGenerationEnabled: aiPrefs.includes('ai_report_generation'),
              aiQaQcEnabled: aiPrefs.includes('ai_qc_review'),
              automaticIcd10TaggingEnabled: aiPrefs.includes('ai_cpt_tagging'),
              ...(aiPrefs.includes('ai_qc_review') ? {
                qaQcAutomationSettings: {
                  draftReports: 'Manual',
                  preliminaryReports: 'Manual',
                  finalReports: 'Manual',
                  revisedReports: 'Manual'
                }
              } : {})
            } : undefined

            const requestData = { ...payload, groupId, containerURL, aiSettings }
            const { responseOk: updateGroupResponseOk, responseJSON: updateGroupResponse } = yield call(postUpdateGroupAPICall, requestData)
            if (updateGroupResponseOk) {
              yield call(postAdminSignUpInfoToZapierAPICall, {
                jsonContent: {
                  ...userResponseJSON,
                  ...updateGroupResponse,
                  ...buildOrganizationDetailsZapierPayload(payload)
                },
                options: { source: groupSourceConstant.creating_new_group }
              })
              if (!payload.userFacility) {
                let facilityContainerURL
                if (userGroupLogo && userGroupLogo[0]) {
                  facilityContainerURL = yield call(getAzureAddressApiCall, 'facilitylogos')
                }
                const { responseOk: createFacilityStatus, responseJSON: createFacilityResponse } = yield call(postCreateFacilityAPICall, { userFacilityName: requestData.userGroupName, userMemberOf: groupId, userFacilityLogo: userGroupLogo, containerURL: facilityContainerURL, hideProgress: true })
                if (createFacilityStatus) {
                  const updatedAppMetadata = {
                    app_metadata: {
                      affiliated_group: groupId,
                      admin: '1',
                      main: '1',
                      approved_facilities: [createFacilityResponse.guid],
                      associated_groups: []
                    }
                  }
                  const { responseOk: patchedUserStatus, responseJSON: patchedUserResponse } = yield call(patchExistingUserAPICall, userId, { appMetadata: updatedAppMetadata })
                  // if (patchedUserStatus) yield call(postNonAdminSignUpInfoToZapierAPICall, { jsonContent: patchedUserResponse, options: { source: groupSourceConstant.creating_new_group } })
                  
                  // Only load sample studies if user explicitly opted in (checkbox checked)
                  // Default is now unchecked (false)
                  if (payload.loadSampleStudies === true) {
                    yield call(postDemoStudyAPICall, userId, createFacilityResponse?.guid)
                  }
                  
                  store.set(LOCAL_STORAGE_NEW_FACILITY_KEY, createFacilityResponse.guid)
                  clearNotification();
                  yield put(postCreateGroupSuccess(createGroupResponse))
                  // Flow finished — clear expedited marker properly (not string "null").
                  localStorage.removeItem(LOCAL_STORAGE_EXPEDITED_SIGNUP)
                  if (!redirect) {
                   payload?.isExpedite ? history.replace(`/expedited-signup/${SIGN_UP_STEPS['expedited-success'].path}`) : history.replace(`/signup/${SIGN_UP_STEPS['success'].path}`)
                  }
                }
              }
            }
          }
          clearNotification();
          if (payload.userFacility) {
            yield put(postCreateFacilityRequest(payload.userFacility))
          }
        }
      }
      if (!payload.isExpedite) yield put(stopSubmit(USER_FORM_NAME));
    }
  } catch (e) {
    clearNotification();
    console.error('ERROR: ', e)

    let modifiedPayload = { ...payload }
    if (response && response?.ip) {
      modifiedPayload.ipAddress = response?.ip
    }
    if (response && response?.country) {
      modifiedPayload.ipAddress = response?.ip
      const singleCountryFound = countryList.find(singleCountry => singleCountry.alpha2Code === response?.country || singleCountry.alpha3Code === response?.country)
      modifiedPayload.countryName = singleCountryFound.name
    }
    if (getUser && getUser?.user_metadata?.phone_number) {
      modifiedPayload.phoneNumber = getUser?.user_metadata?.phone_number      
    }
    if (getUser && getUser?.email) {
      modifiedPayload.email = getUser?.email      
    }    
    yield call(postNotificationToSlackAPICall, { title: '[Create Organization]:', description: `Failed to create organization: ${e.message}`, jsonContent: JSON.stringify(modifiedPayload, null, 2) })
    if (!payload.isExpedite) yield put(stopSubmit(USER_FORM_NAME, {
      userGroupName: 'Failed to create group'
    }));
    yield put({
      type: POST_CREATE_GROUP_FAILURE,
      message: e.message
    })
  }
}

function* postAssignGroupHandler({ payload }) {
  try {
    dataLoadingNotification(t("group_saga.processing"))
    const auth = new AuthService()
    const userProfile = auth.getUserProfile()
    const { redirect = false, groupId: payloadGroupId } = payload || {}
    const response = yield call(getCurrentIPInfoAPICall);
    const country = response?.country ?? null;
    const countryList = store.get(LOCAL_STORAGE_COUNTRY_LIST_KEY)
    let countryName = null;

    if (country && countryList) {
      const singleCountryFound = countryList.find(singleCountry => singleCountry.alpha2Code === country || singleCountry.alpha3Code === country)
      countryName = singleCountryFound.name
    }

    const groupId = payloadGroupId || store.get(LOCAL_STORAGE_GROUP_ID)
    if (!isEmpty(groupId)) {
      // store.set(LOCAL_STORAGE_NEW_ORGANIZATION_KEY, groupId)
      // store.set(INVITE_ORG, { groupId, groupName: userGroupName })
      const userId = userProfile && userProfile.sub
      const updatedAppMetadata = {
        app_metadata: {
          affiliated_group: groupId,
          approved_facilities: [],
          associated_groups: []
        }
      }
      const { responseOk: userUpdateOk, responseJSON: userResponseJSON } = yield call(patchExistingUserAPICall, userId, { appMetadata: updatedAppMetadata })
      if (userUpdateOk) {
        yield call(postNonAdminSignUpInfoToZapierAPICall, { jsonContent: userResponseJSON, options: { source: groupSourceConstant.joining_existing_group } })
        clearNotification();
        yield put(postAssignGroupSuccess())
        if (!redirect) {
          history.replace(`/signup/${SIGN_UP_STEPS['success'].path}`)
        } else {
          history.replace(redirect)
        }
      }
    } else {
      throw new Error('Group ID not found')
    }
  } catch (e) {
    clearNotification();
    console.error('ERROR: ', e)
    yield call(postNotificationToSlackAPICall, { title: '[Create Organization]:', description: `Failed to assign organization: ${e.message}`, jsonContent: payload })
    yield put({
      type: POST_ASSIGN_GROUP_FAILURE,
      message: e.message
    })
  }
}

function * updateGroupSaga ({ payload }) {
  try {
    const { responseOk, responseJSON } = yield call(postUpdateGroupAPICall, payload)

    if(responseOk){
      const { name, group_id } = responseJSON
      store.set(INVITE_ORG, { groupId: group_id, groupName: name })
      history.replace(`/signup/${SIGN_UP_STEPS['invite'].path}`)
    }
  } catch (e) {
    console.error(e)
    yield put({
      type: UPDATE_GROUP_FAILURE,
      message: e.message
    })
  }
}

export function * groupSaga () {
  yield takeLatest(GET_GROUP_REQUEST, getGroupHandler)
  yield takeLatest(GET_CHECK_GROUP_REQUEST, getCheckGroupHandler)
  yield takeLatest(POST_CREATE_GROUP_REQUEST, postCreateGroupHandler)
  yield takeLatest(POST_ASSIGN_GROUP_REQUEST, postAssignGroupHandler)
  yield takeLatest(UPDATE_GROUP_REQUEST, updateGroupSaga)
}
